<script setup lang="ts">
import { useDeckPlayerContext } from '../../composables/useDeckPlayerShell'
import { vimeoEmbedUrl, youtubeEmbedUrl } from '../../flowchart/runtime/media'
import NowPlayingControls from './NowPlayingControls.vue'

/** Media over a dark backdrop with the card visible behind (modal mode). */
const { t, resolvedMedia, nowPlaying, runner } = useDeckPlayerContext()
const { dismissShowMedia } = runner
const { showingMedia: graphShowingMedia } = useDeckPlayerContext().state
const {
  initYoutube: initialiseYoutubePlayer,
  initVimeo: initialiseVimeoPlayer,
  logNativeEvent: logNativeMediaEvent,
  onNativeEnded: handleNativeMediaEnded,
  onNativePlay: handleNowPlayingMediaPlay,
  onNativePause: handleNowPlayingMediaPause,
  bindMediaEl,
  bindYoutubeFrame,
  bindVimeoFrame,
} = nowPlaying
</script>

<template>
  <Teleport to="body">
    <div
      v-if="graphShowingMedia && resolvedMedia?.displayMode === 'modal'"
      class="pfc-media-modal-backdrop"
      @click.self="resolvedMedia?.skippable !== false && dismissShowMedia()"
    >
      <button
        v-if="resolvedMedia.skippable"
        class="pfc-media-modal-continue"
        @click="dismissShowMedia"
      >
        {{ t('common.continue') }} ›
      </button>
      <img
        v-if="resolvedMedia.mediaType === 'image'"
        :src="resolvedMedia.primaryUrl || undefined"
        class="pfc-media-modal-img"
        :alt="resolvedMedia.mediaName"
      />
      <div v-else-if="resolvedMedia.mediaType === 'video'" class="pfc-media-modal-video-wrap">
        <iframe
          v-if="youtubeEmbedUrl(resolvedMedia.primaryUrl, !resolvedMedia.skippable)"
          :ref="bindYoutubeFrame"
          :src="youtubeEmbedUrl(resolvedMedia.primaryUrl, !resolvedMedia.skippable)!"
          class="pfc-media-modal-video"
          allow="
            accelerometer;
            autoplay;
            clipboard-write;
            encrypted-media;
            gyroscope;
            picture-in-picture;
          "
          allowfullscreen
          @load="initialiseYoutubePlayer"
        />
        <iframe
          v-else-if="vimeoEmbedUrl(resolvedMedia.primaryUrl, !resolvedMedia.skippable)"
          :ref="bindVimeoFrame"
          :src="vimeoEmbedUrl(resolvedMedia.primaryUrl, !resolvedMedia.skippable)!"
          class="pfc-media-modal-video"
          allow="
            accelerometer;
            autoplay;
            clipboard-write;
            encrypted-media;
            gyroscope;
            picture-in-picture;
          "
          allowfullscreen
          @load="initialiseVimeoPlayer"
        />
        <video
          v-else-if="resolvedMedia.primaryUrl"
          :ref="bindMediaEl"
          :src="resolvedMedia.primaryUrl"
          :controls="resolvedMedia.skippable"
          :autoplay="!resolvedMedia.skippable"
          class="pfc-media-modal-video"
          @play="handleNowPlayingMediaPlay"
          @pause="handleNowPlayingMediaPause"
          @seeking="logNativeMediaEvent"
          @seeked="logNativeMediaEvent"
          @ratechange="logNativeMediaEvent"
          @ended="handleNativeMediaEnded"
        />
        <NowPlayingControls
          v-if="!resolvedMedia.skippable && resolvedMedia.primaryUrl"
          kind="video"
        />
      </div>
      <div v-else-if="resolvedMedia.mediaType === 'audio'" class="pfc-media-modal-audio-wrap">
        <NowPlayingControls v-if="!resolvedMedia.skippable" kind="audio" />
        <audio
          v-if="resolvedMedia.primaryUrl"
          :ref="bindMediaEl"
          :src="resolvedMedia.primaryUrl"
          :controls="resolvedMedia.skippable"
          :autoplay="!resolvedMedia.skippable"
          :class="['pfc-media-audio', { 'pfc-media-audio--hidden': !resolvedMedia.skippable }]"
          @play="handleNowPlayingMediaPlay"
          @pause="handleNowPlayingMediaPause"
          @seeking="logNativeMediaEvent"
          @seeked="logNativeMediaEvent"
          @ratechange="logNativeMediaEvent"
          @ended="handleNativeMediaEnded"
        />
        <div v-else class="pfc-media-placeholder">{{ resolvedMedia.mediaName }}</div>
      </div>
      <iframe
        v-else-if="resolvedMedia.mediaType === 'pdf'"
        :src="resolvedMedia.primaryUrl || undefined"
        class="pfc-media-modal-pdf"
      />
      <div v-else class="pfc-media-placeholder" style="color: #fff">
        {{ resolvedMedia.mediaName }}
      </div>
    </div>
  </Teleport>
</template>

<style scoped src="./styles/playerMedia.css"></style>
